"use client";

import { createContext, useContext, useEffect, useState, useCallback, useRef, PropsWithChildren } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from './auth-context';
import { Theme, ThemeTokens, ColorTokens } from '@/types/theme';
import {
  getDefaultPlatformTheme,
  getTheme,
  getPageTheme,
  setUserProfileTheme,
} from '@/services/theme';
import {
  applyThemeTokensToCss,
  normalizeSidebarSurfaceVars,
  clearThemeInlineStyles,
  applyBackgrounds,
  loadFont,
  mergeThemeWithOverrides,
  getPageKeyFromPathname,
} from '@/lib/theme-utils';
import { buildRoleRotation } from '@/lib/theme-rotation';
import { themePresets, plainLightTokens, plainDarkTokens, type ThemeCategory } from '@/lib/theme-presets';
import { randomPalette } from '@/lib/random-palette';
import { getAppearanceSettings, applyAppearanceCss } from '@/services/appearance-settings';
import { subscribeThemeCustomizations } from '@/services/theme-customizations';
import type { ThemeColorOverride } from '@/services/theme-customizations';
import { subscribeGifSkins } from '@/services/gif-skins';
import { db } from '@/lib/firebase';
import { doc as fsDoc, getDoc as fsGetDoc, setDoc as fsSetDoc } from 'firebase/firestore';
import type { AdminGifSkin } from '@/services/gif-skins';

// ── GIF Library types ─────────────────────────────────────────────────────────
export interface GifEntry {
  id: string;
  url: string;
  label?: string;
}

export const GIF_SECTIONS = ['body', 'header', 'sidebar', 'card', 'player'] as const;
export type GifSection = (typeof GIF_SECTIONS)[number];

// ── GIF Skin types ────────────────────────────────────────────────────────────
/**
 * @deprecated Use AdminGifSkin from @/services/gif-skins instead.
 * Kept for backward compatibility with theme-picker.tsx.
 */
export type GifSkinCategory = string;

/** @deprecated Category labels are now derived dynamically from Firestore skins */
export const GIF_SKIN_CATEGORY_LABELS: Record<string, string> = {};

export interface GifSkin {
  id: string;
  name: string;
  /** Free-text category (e.g. "Nature", "Neon") */
  category: string;
  bodyUrl: string;
  bodyOpacity?: number;
  baseHue: number;
  enabled?: boolean;
}

/**
 * Empty — all GIF skins are now managed via Firestore (Super Admin → GIF Skins tab).
 * @deprecated Kept for import compatibility only; always empty at runtime.
 */
export const GIF_SKINS: GifSkin[] = [];

// ── DOM helpers for Image/Video skin backdrop ─────────────────────────────────
const GIF_BACKDROP_ID = 'bb-gif-skin-backdrop';
const GIF_SKIN_STYLE_ID = 'bb-gif-skin-styles';

/** Detect whether a skin URL is a YouTube link, Vimeo link, direct video, or image/GIF */
function detectSkinMediaType(url: string): 'youtube' | 'vimeo' | 'video' | 'image' {
  if (!url) return 'image';
  if (/youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed/.test(url)) return 'youtube';
  if (/vimeo\.com\//.test(url)) return 'vimeo';
  if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)) return 'video';
  return 'image';
}

function getYouTubeEmbedUrl(url: string): string {
  const match = url.match(/(?:v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  if (!match) return '';
  const id = match[1];
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&disablekb=1&modestbranding=1&showinfo=0&rel=0&playsinline=1`;
}

function getVimeoEmbedUrl(url: string): string {
  const match = url.match(/vimeo\.com\/(\d+)/);
  if (!match) return '';
  return `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=1&loop=1&background=1&controls=0`;
}

/** Get a static thumbnail URL for a video skin (used in picker cards) */
export function getSkinThumbnailUrl(bodyUrl: string): string {
  const type = detectSkinMediaType(bodyUrl);
  if (type === 'youtube') {
    const match = bodyUrl.match(/(?:v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
    if (match) return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
  }
  if (type === 'vimeo') {
    // Vimeo thumbnails require an API call; fall back to a placeholder
    return '';
  }
  // For images/GIFs/direct video: use the URL itself (img tag handles it)
  return type === 'video' ? '' : bodyUrl;
}

// Background demo (Gordon 2026-11-02: "images could slowly be animated in the
// background and change, as a demo setting"): picture backdrops drift (slow
// zoom + pan) while it is on; the provider changes them every 25 s.
let bgDrift = false;
const BG_DEMO_MS = 25_000;

// The Random button's background pool (Gordon 2026-11-02: "add gifs bg in the
// random background generator, including releases' artwork, YouTube BB videos,
// gifs") — loaded once per visit, on first use.
let artPool: Promise<{ url: string; name: string }[]> | null = null;
let videoPool: Promise<{ url: string; name: string }[]> | null = null;
function loadArtPool() {
  artPool ??= import('@/services/home')
    .then(m => m.getCrateReleases())
    .then(rs => rs.filter(r => r.imageUrl).map(r => ({ url: r.imageUrl, name: r.title })))
    .catch(() => []);
  return artPool;
}
function loadVideoPool() {
  videoPool ??= import('@/services/videos')
    .then(m => m.getLatestVideos(40))
    .then(vs => vs.filter(v => v.youtubeId).map(v => ({ url: `https://www.youtube.com/watch?v=${v.youtubeId}`, name: v.title || 'BB video' })))
    .catch(() => []);
  return videoPool;
}

function applyGifSkinToDom(skin: GifSkin, gifOpacity?: number, gifBgColor?: string, gifTextColor?: string, gifBold?: boolean) {
  if (typeof document === 'undefined') return;
  // Remove existing backdrop
  document.getElementById(GIF_BACKDROP_ID)?.remove();
  document.getElementById(GIF_SKIN_STYLE_ID)?.remove();

  const opacity = gifOpacity ?? skin.bodyOpacity ?? 0.72;
  const bgColor = gifBgColor || 'transparent';
  const textRule = gifTextColor ? `color: ${gifTextColor} !important;` : '';
  const boldRule = gifBold ? 'font-weight: bold !important;' : '';
  const mediaType = detectSkinMediaType(skin.bodyUrl);

  let backdrop: HTMLElement;

  if (mediaType === 'youtube') {
    const embedUrl = getYouTubeEmbedUrl(skin.bodyUrl);
    const wrapper = document.createElement('div');
    wrapper.id = GIF_BACKDROP_ID;
    wrapper.style.cssText = 'position:fixed;inset:0;z-index:-1;pointer-events:none;overflow:hidden;';
    if (embedUrl) {
      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.allow = 'autoplay; encrypted-media';
      iframe.setAttribute('allowfullscreen', '');
      iframe.style.cssText = `position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);` +
        `width:100vw;height:56.25vw;min-height:100vh;min-width:177.78vh;` +
        `border:none;opacity:${opacity};`;
      wrapper.appendChild(iframe);
    }
    backdrop = wrapper;
  } else if (mediaType === 'vimeo') {
    const embedUrl = getVimeoEmbedUrl(skin.bodyUrl);
    const wrapper = document.createElement('div');
    wrapper.id = GIF_BACKDROP_ID;
    wrapper.style.cssText = 'position:fixed;inset:0;z-index:-1;pointer-events:none;overflow:hidden;';
    if (embedUrl) {
      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.allow = 'autoplay; encrypted-media';
      iframe.style.cssText = `position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);` +
        `width:100vw;height:56.25vw;min-height:100vh;min-width:177.78vh;` +
        `border:none;opacity:${opacity};`;
      wrapper.appendChild(iframe);
    }
    backdrop = wrapper;
  } else if (mediaType === 'video') {
    const wrapper = document.createElement('div');
    wrapper.id = GIF_BACKDROP_ID;
    wrapper.style.cssText = 'position:fixed;inset:0;z-index:-1;pointer-events:none;overflow:hidden;';
    const video = document.createElement('video');
    video.src = skin.bodyUrl;
    video.autoplay = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    (video as HTMLVideoElement & { disablePictureInPicture?: boolean }).disablePictureInPicture = true;
    video.style.cssText = `position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;opacity:${opacity};`;
    wrapper.appendChild(video);
    backdrop = wrapper;
  } else {
    // Image / GIF — original background-image approach
    const div = document.createElement('div');
    div.id = GIF_BACKDROP_ID;
    div.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:-100',
      `background-image:url('${skin.bodyUrl}')`,
      'background-size:cover', 'background-position:center',
      `opacity:${opacity}`, 'pointer-events:none',
      `animation:gifSkinFadeIn 0.8s ease-out${bgDrift ? ', bbBgDrift 40s ease-in-out infinite alternate' : ''}`,
      'transform-origin:50% 50%',
    ].join(';');
    backdrop = div;
  }

  document.body.insertBefore(backdrop, document.body.firstChild);

  // CSS: make [data-bg="body"] transparent so backdrop shows through
  const style = document.createElement('style');
  style.id = GIF_SKIN_STYLE_ID;
  style.textContent = `
    @keyframes gifSkinFadeIn { from { opacity: 0; } to { opacity: ${opacity}; } }
    @keyframes bbBgDrift { from { transform: scale(1.06) translate(0, 0); } to { transform: scale(1.2) translate(-2.5%, -2%); } }
    @media (prefers-reduced-motion: reduce) { #${GIF_BACKDROP_ID} { animation: none !important; } }
    [data-bg="body"] { background-color: ${bgColor} !important; ${textRule} ${boldRule} }
    [data-bg="sidebar"] { background-color: hsl(var(--sidebar-background) / 0.88) !important; }
    [data-bg="header"]  { background-color: hsl(var(--background) / 0.92) !important; backdrop-filter: blur(10px); }
  `;
  document.head.appendChild(style);
}

function clearGifSkinFromDom() {
  if (typeof document === 'undefined') return;
  document.getElementById(GIF_BACKDROP_ID)?.remove();
  document.getElementById(GIF_SKIN_STYLE_ID)?.remove();
}

/** localStorage.getItem that can never throw — on iOS restricted-storage
 *  modes bare storage access throws SecurityError, and several reads below
 *  run inside useState initializers (i.e. during render), where a throw
 *  takes down the whole ThemeProvider at boot. */
function lsGetSafe(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSetSafe(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}
function lsRemoveSafe(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/** Every visit starts on BB's plain monochrome theme, dark or light as the
 *  device's system setting says (Gordon 2026-11-02: "default is system setting
 *  theme dark or light in monochrome grey scale"; before that always dark).
 *  Nothing else changes colour by itself: no stored guest theme, no random
 *  skins ("it steals a little from the artwork"). What a visitor picks in the
 *  Themes panel applies for that visit only, so a demo is undone by a reload.
 *  Reads the flag auth writes (also used pre-paint in layout.tsx), so a
 *  signed-in boot never flashes the visitor look first. */
function isVisitorNow(): boolean {
  return lsGetSafe('bb-signed-in') !== '1';
}
/** A visitor's light/dark pick in the Themes panel — this visit only. */
let visitorModePick: 'dark' | 'light' | 'system' | null = null;
function visitorPrefersDark(systemDark: boolean): boolean {
  if (visitorModePick === 'light') return false;
  if (visitorModePick === 'dark') return true;
  return systemDark;
}

const LS_GUEST_THEME    = 'bb-guest-theme-id';
const LS_COLOR_ROTATION = 'bb-color-rotation';
const LS_GIF_ENABLED    = 'bb-gif-theme-enabled';
const LS_GIF_LIBRARY    = 'bb-gif-library';
const LS_SECTION_GIFS   = 'bb-section-gifs';
const LS_ACTIVE_SKIN    = 'bb-active-skin-id';
const LS_GIF_OPACITY    = 'bb-gif-opacity';
const LS_GIF_BG_COLOR   = 'bb-gif-bg-color';
const LS_GIF_TEXT_COLOR = 'bb-gif-text-color';
const LS_GIF_BOLD       = 'bb-gif-bold';
const LS_GIF_SIZE       = 'bb-gif-size';
const LS_GIF_TILE_SIZE  = 'bb-gif-tile-size';

export type GifSizeMode = 'cover' | 'fill' | 'contain' | 'tile' | 'original';

// GIF library — starts empty; populated by admins via Firestore or user uploads
const DEFAULT_GIF_LIBRARY: GifEntry[] = [];

// ── HSL helpers ───────────────────────────────────────────────────────────────
function hslLightness(hsl: string): number | null {
  const p = hsl.trim().split(/\s+/);
  return p.length >= 3 ? parseFloat(p[2]) : null;
}

function hslSaturation(hsl: string): number | null {
  const p = hsl.trim().split(/\s+/);
  return p.length >= 3 ? parseFloat(p[1]) : null;
}

/** Shift hue by `degrees` (wraps 0-360) */
function shiftHue(hsl: string, degrees: number): string {
  const p = hsl.trim().split(/\s+/);
  if (p.length < 3) return hsl;
  const h = ((parseFloat(p[0]) + degrees) % 360 + 360) % 360;
  return `${Math.round(h)} ${p[1]} ${p[2]}`;
}

/** Returns true if ALL non-transparent colors in the palette have saturation < 8% */
function isMonochromatic(colors: ColorTokens | undefined | null): boolean {
  if (!colors) return true; // treat missing colors as monochromatic (safe fallback)
  const relevant = [
    colors.background, colors.foreground, colors.primary, colors.accent,
    colors.secondary, colors.sidebarBackground, colors.sidebarPrimary,
  ];
  return relevant
    .filter(Boolean)
    .every(v => (hslSaturation(v as string) ?? 0) < 8);
}

/** Shift ALL hue values in a ColorTokens object by `degrees` */
function shiftAllHues(colors: ColorTokens, degrees: number): ColorTokens {
  const result = { ...colors } as Record<string, string>;
  for (const [k, v] of Object.entries(colors)) {
    if (typeof v === 'string' && v.trim().split(/\s+/).length >= 3) {
      result[k] = shiftHue(v, degrees);
    }
  }
  return result as unknown as ColorTokens;
}

// ── Country theme colour-swap rotation ────────────────────────────────────────

/** Nudge lightness by `delta` (clamped 2–97). */
function adjustLightness(hsl: string, delta: number): string {
  const p = hsl.trim().split(/\s+/);
  if (p.length < 3) return hsl;
  const l = Math.max(2, Math.min(97, parseFloat(p[2]) + delta));
  return `${p[0]} ${p[1]} ${l.toFixed(1)}%`;
}

/** Return a near-black or near-white depending on bg lightness. */
function contrastFg(hsl: string): string {
  const p = hsl.trim().split(/\s+/);
  const l = p.length >= 3 ? parseFloat(p[2]) : 50;
  return l > 45 ? '0 0% 5%' : '0 0% 95%';
}

/**
 * Build a rotated variant of a country theme by cycling the three
 * flag colours through the bg / primary-button / accent roles.
 *
 *   step 0 (default) : [bg=dark,    btn=flag1, accent=flag2]
 *   step 1           : [bg=flag1,   btn=flag2, accent=dark ]
 *   step 2           : [bg=flag2,   btn=dark,  accent=flag1]
 */
function buildCountryVariant(colors: ColorTokens | undefined | null, step: number): ColorTokens {
  if (!colors || step === 0) return (colors ?? {}) as ColorTokens;

  const bg0      = colors.background;
  const primary0 = colors.primary;
  const accent0  = colors.accent || colors.primary;

  // Left-rotate the triple by `step` positions
  const slots = [bg0, primary0, accent0];
  const newBg      = slots[(0 + step) % 3];
  const newPrimary = slots[(1 + step) % 3];
  const newAccent  = slots[(2 + step) % 3];

  const newFg         = contrastFg(newBg);
  const newPrimaryFg  = contrastFg(newPrimary);
  const newAccentFg   = contrastFg(newAccent);

  const bgL = parseFloat(newBg.trim().split(/\s+/)[2] ?? '50');
  const cardDelta   = bgL < 50 ? +5  : -6;
  const mutedDelta  = bgL < 50 ? +3  : -10;
  const borderDelta = bgL < 50 ? +12 : -18;

  const newCard   = adjustLightness(newBg, cardDelta);
  const newMuted  = adjustLightness(newBg, mutedDelta);
  const newBorder = adjustLightness(newBg, borderDelta);
  const newSidebar = adjustLightness(newBg, bgL < 50 ? -3 : -5);

  return {
    ...colors,
    background:               newBg,
    foreground:               newFg,
    card:                     newCard,
    cardForeground:           newFg,
    popover:                  newCard,
    popoverForeground:        newFg,
    headerBackground:         newPrimary,
    primary:                  newPrimary,
    primaryForeground:        newPrimaryFg,
    accent:                   newAccent,
    accentForeground:         newAccentFg,
    secondary:                newMuted,
    secondaryForeground:      newFg,
    muted:                    newMuted,
    mutedForeground:          bgL < 50 ? '0 0% 58%' : '0 0% 42%',
    border:                   newBorder,
    input:                    newBorder,
    ring:                     newPrimary,
    playerToggleBackground:   newAccent,
    sidebarBackground:        newSidebar,
    sidebarForeground:        newFg,
    sidebarPrimary:           newPrimary,
    sidebarPrimaryForeground: newPrimaryFg,
    sidebarAccent:            newAccent,
    sidebarAccentForeground:  newAccentFg,
    sidebarBorder:            newBorder,
    sidebarRing:              newPrimary,
  };
}

/** Invert lightness of all colors (creates a dark↔light swap) */
function invertLightness(colors: ColorTokens): ColorTokens {
  const result = { ...colors } as Record<string, string>;
  for (const [k, v] of Object.entries(colors)) {
    if (typeof v === 'string') {
      const p = v.trim().split(/\s+/);
      if (p.length >= 3) {
        const l = parseFloat(p[2]);
        result[k] = `${p[0]} ${p[1]} ${(100 - l).toFixed(1)}%`;
      }
    }
  }
  return result as unknown as ColorTokens;
}

// ── Context type ──────────────────────────────────────────────────────────────
interface ThemeContextType {
  currentTheme: Theme | null;
  setUserTheme: (themeId: string | null) => Promise<void>;
  reloadTheme: () => Promise<void>;
  loading: boolean;
  // Color rotation
  colorRotationEnabled: boolean;
  setColorRotationEnabled: (enabled: boolean) => void;
  /** Manually advance one rotation step (15° hue shift or lightness inversion) */
  rotateOneStep: () => void;
  /** Generate and apply a random high-contrast color theme */
  applyRandomColorTheme: () => void;
  // GIF theme (library / per-section)
  gifThemeEnabled: boolean;
  gifLibrary: GifEntry[];
  bgFxSound: boolean;
  setBgFxSound: (v: boolean) => void;
  bgFxTilt: boolean;
  setBgFxTilt: (v: boolean) => void;
  /** Per-section pinned GIF URLs (section key → URL). Empty = use random from library. */
  sectionGifs: Record<string, string>;
  setGifThemeEnabled: (enabled: boolean) => void;
  updateGifLibrary: (gifs: GifEntry[]) => void;
  /** Pin (or unpin when url=null) a specific GIF to a section */
  setSectionGif: (section: string, url: string | null) => void;
  /** Remove all per-section GIF assignments */
  clearSectionGifs: () => void;
  /** Immediately pick a new random GIF for every section from the library */
  randomizeGifSections: () => void;
  // GIF skins (named full-app GIF themes)
  /** All GIF skins loaded from Firestore (Super Admin managed) */
  gifSkins: GifSkin[];
  activeSkinId: string | null;
  /** Apply a named GIF skin as the full app background */
  applyGifSkin: (skinId: string) => void;
  /** Pick and apply a random GIF skin */
  applyRandomGifSkin: (opts?: { persist?: boolean }) => void;
  /** A random background from the mixed pool: Image Skins, the GIF library, release artwork, BB videos. */
  applyRandomBackdrop: (opts?: { persist?: boolean }) => Promise<void>;
  /** Background demo: backdrops drift slowly and change every 25 s. */
  bgDemo: boolean;
  setBgDemo: (on: boolean) => void;
  /** Remove the active GIF skin and restore solid background */
  clearGifSkin: () => void;
  // GIF visual controls
  /** Opacity of the GIF overlay (0–1). Default 0.6 */
  gifOpacity: number;
  /** Background colour shown behind the GIF (hex / empty = transparent) */
  gifBgColor: string;
  /** Text colour override for GIF-covered areas (hex / empty = default) */
  gifTextColor: string;
  /** Whether to bold text in GIF-covered areas */
  gifBold: boolean;
  setGifOpacity: (v: number) => void;
  setGifBgColor: (v: string) => void;
  setGifTextColor: (v: string) => void;
  setGifBold: (v: boolean) => void;
  /** How the GIF fills its container: cover | fill | contain | tile | original */
  gifSize: GifSizeMode;
  setGifSize: (v: GifSizeMode) => void;
  /** Tile size in px (used when gifSize === 'tile') */
  gifTileSize: number;
  setGifTileSize: (v: number) => void;
  /**
   * Immediately apply a dark/light/system appearance mode.
   * Updates localStorage, toggles the `dark` CSS class, and re-applies all
   * theme tokens synchronously — no event dispatch, no async round-trips.
   */
  setAppearanceMode: (mode: 'dark' | 'light' | 'system') => void;
  /** Currently active appearance mode ('dark' | 'light' | 'system') */
  appearanceMode: 'dark' | 'light' | 'system';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Pick the correct tokens based on the OS color-scheme preference.
 * - prefersDark + darkTokens  → use darkTokens  (light-base theme following system to dark)
 * - !prefersDark + lightTokens → use lightTokens (dark-base theme following system to light)
 * - otherwise → use the theme's default tokens
 */
// forceOwnTokens (default true): skip the mode-conflict fallback so any chosen
// theme always renders its own colours. Pass false only for uninitialised state
// where no theme has been explicitly selected.
function resolveTokens(theme: Theme, prefersDark: boolean, forceOwnTokens = true): ThemeTokens {
  if (prefersDark && theme.darkTokens) {
    return theme.darkTokens;
  }
  if (!prefersDark && theme.lightTokens) {
    return theme.lightTokens;
  }

  // When the user actively selects a theme, always use that theme's tokens.
  // The mode-conflict fallback below only applies to the system/welcome-modal
  // dark-light toggle when no specific theme has been actively chosen.
  // NOTE: no `typeof localStorage` guard — on blocked-storage devices even
  // that check throws (the window.localStorage getter throws on access).
  // lsGetSafe handles both SSR and blocked storage by returning null.
  if (!forceOwnTokens && typeof window !== 'undefined') {
    const explicitMode = lsGetSafe('bb-theme-mode');
    if (explicitMode === 'light' || explicitMode === 'dark') {
      const bg = theme.tokens?.colors?.background ?? '';
      const bgParts = bg.trim().split(/\s+/);
      const bgL = parseFloat(bgParts[2] ?? '50');
      const themeIsDark = bgL < 50;

      if (explicitMode === 'light' && themeIsDark) {
        return {
          ...plainLightTokens,
          typography: theme.tokens?.typography ?? plainLightTokens.typography,
          spacing:    theme.tokens?.spacing    ?? plainLightTokens.spacing,
          radius:     theme.tokens?.radius     ?? plainLightTokens.radius,
        };
      }
      if (explicitMode === 'dark' && !themeIsDark) {
        return {
          ...plainDarkTokens,
          typography: theme.tokens?.typography ?? plainDarkTokens.typography,
          spacing:    theme.tokens?.spacing    ?? plainDarkTokens.spacing,
          radius:     theme.tokens?.radius     ?? plainDarkTokens.radius,
        };
      }
    }
  }

  return theme.tokens;
}

/**
 * Apply customizations (color token overrides) to a theme.
 */
function applyCustomizations(theme: Theme, customizations: Record<string, ThemeColorOverride>): Theme {
  const override = customizations[theme.id];
  if (!override) return theme;

  return {
    ...theme,
    tokens: override.colors
      ? { ...theme.tokens, colors: { ...(theme.tokens?.colors || {}), ...override.colors } }
      : theme.tokens,
    darkTokens: (theme.darkTokens && override.darkColors)
      ? { ...theme.darkTokens, colors: { ...(theme.darkTokens.colors || {}), ...override.darkColors } }
      : theme.darkTokens,
    lightTokens: ((theme as any).lightTokens && override.lightColors)
      ? { ...(theme as any).lightTokens, colors: { ...((theme as any).lightTokens.colors || {}), ...override.lightColors } }
      : (theme as any).lightTokens,
  };
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const { user, userProfile } = useAuth();
  const pathname = usePathname();
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(null);
  // A theme picked again starts its colour cycle from the top.
  useEffect(() => { countryStepRef.current = {}; }, [currentTheme?.id]);
  // Low-vision mode (Gordon 2026-11-02: "anpassa app för synskadade"): every
  // accessibility theme also changes how the app behaves — html[data-a11y] in
  // globals.css (solid surfaces, no tiny text, thick focus rings, no motion).
  useEffect(() => {
    const on = !!currentTheme && themePresets.find(p => p.id === currentTheme.id)?.category === 'accessibility';
    try {
      if (on) document.documentElement.setAttribute('data-a11y', 'low-vision');
      else document.documentElement.removeAttribute('data-a11y');
    } catch { /* ignore */ }
  }, [currentTheme?.id]);
  // "bb-low-vision" {on} — coπ ("turn on low vision", "stor text") and the Why BB
  // button. Turning it off brings back the theme the person had before.
  const setUserThemeRef = useRef(setUserTheme);
  setUserThemeRef.current = setUserTheme;
  useEffect(() => {
    const onLowVision = (e: Event) => {
      const on = !!(e as CustomEvent<{ on?: boolean }>).detail?.on;
      const cur = currentThemeRef.current?.id ?? '';
      if (on) {
        if (cur === 'low-vision') return;
        lsSetSafe('bb-before-low-vision', cur);
        setUserThemeRef.current('low-vision').catch(() => {});
      } else {
        const prev = lsGetSafe('bb-before-low-vision');
        lsRemoveSafe('bb-before-low-vision');
        setUserThemeRef.current(prev && prev !== 'low-vision' ? prev : null).catch(() => {});
      }
    };
    window.addEventListener('bb-low-vision', onLowVision);
    return () => window.removeEventListener('bb-low-vision', onLowVision);
  }, []);
  const [loading, setLoading] = useState(true);
  // Initialized to the REAL preference immediately (saved mode → OS setting).
  // Starting at `false` meant a dark-mode user's first theme application used
  // LIGHT tokens, then flipped to dark when the effect computed the truth —
  // one of the boot-time theme flashes. Hydration-safe: prefersDark never
  // affects render output, only effect-driven CSS var application.
  const [prefersDark, setPrefersDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (isVisitorNow()) return visitorPrefersDark(!!window.matchMedia?.('(prefers-color-scheme: dark)').matches);
      const saved = lsGetSafe('bb-theme-mode');
      if (saved === 'dark') return true;
      if (saved === 'light') return false;
      return !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    } catch { return false; }
  });
  const [customizations, setCustomizations] = useState<Record<string, ThemeColorOverride>>({});

  // Sidebar-follows-theme pass on mount — catches the boot where the
  // pre-paint script replayed a STALE cached black sidebar onto a light
  // theme and no theme application runs to correct it (Gordon: "light
  // theme and a dark side menu???").
  useEffect(() => { try { normalizeSidebarSurfaceVars(); } catch { /* ignore */ } }, []);

  // Refs so event-handler closures always see the latest values without stale-closure issues.
  // Updated on every render (safe — React renders are synchronous).
  const currentThemeRef = useRef<Theme | null>(null);
  const prefersDarkRef  = useRef(false);
  const customizationsRef = useRef<Record<string, ThemeColorOverride>>({});
  currentThemeRef.current  = currentTheme;
  prefersDarkRef.current   = prefersDark;
  customizationsRef.current = customizations;

  // Color rotation state — always off; guests see stable dark/light themes only
  const [colorRotationEnabled, setColorRotationEnabledState] = useState<boolean>(false);
  const hueOffsetRef = useRef(0);
  const lastPaletteFamilyRef = useRef<string | undefined>(undefined);
  const invertedRef = useRef(false);
  // Per-themeId step counter for country colour-swap rotation (0–2)
  const countryStepRef = useRef<Record<string, number>>({});

  // GIF theme state
  const [gifThemeEnabled, setGifThemeEnabledState] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return lsGetSafe(LS_GIF_ENABLED) === 'true';
    }
    return false;
  });
  const [gifLibrary, setGifLibrary] = useState<GifEntry[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = lsGetSafe(LS_GIF_LIBRARY);
        if (stored) return JSON.parse(stored);
      } catch { /* ignore */ }
    }
    return DEFAULT_GIF_LIBRARY;
  });

  // ── BB shared GIF library ────────────────────────────────────────────────
  // The SUPER ADMIN's personal "My GIFs" list doubles as the platform-wide
  // "BB GIFs" library (Gordon: "super admin GIFs is BB GIF's for all other
  // users"). It publishes to siteSettings/bbGifLibrary (world-readable,
  // admin-writable) and merges read-only into every other user's library —
  // so pinning, per-section randomize and the random start-page theme all
  // draw from it automatically.
  const [bbGifs, setBbGifs] = useState<GifEntry[]>([]);
  const isSuperAdminUser = !!(
    user?.email === 'breakinbreadswe@gmail.com' ||
    userProfile?.accountType === 'superadmin' ||
    (Array.isArray(userProfile?.roles) && (userProfile!.roles as string[]).some(r => r?.toLowerCase?.() === 'superadmin'))
  );
  // BB's default library = the shared list + EVERY GIF/WebP a BB admin ever
  // uploaded (/api/theme/bb-media lists them from Storage, edge-cached), so it
  // is the same for visitors and users and can't shrink by accident (Gordon
  // 2026-11-02: the shared list had been overwritten down to 1 of 531 files).
  useEffect(() => {
    let alive = true;
    Promise.all([
      fsGetDoc(fsDoc(db, 'siteSettings', 'bbGifLibrary'))
        .then(snap => (snap.exists() ? ((snap.data() as any).gifs || []) as GifEntry[] : []))
        .catch(() => [] as GifEntry[]),
      fetch('/api/theme/bb-media').then(r => (r.ok ? r.json() : { items: [] })).then(j => (j.items || []) as GifEntry[]).catch(() => [] as GifEntry[]),
    ]).then(([shared, uploaded]) => {
      if (!alive) return;
      const seen = new Set<string>();
      const all: GifEntry[] = [];
      for (const g of [...shared.map(g => ({ ...g, id: `bbgif:${g.id}` })), ...uploaded]) {
        if (!g.url || seen.has(g.url.split('?')[0])) continue;
        seen.add(g.url.split('?')[0]);
        all.push(g);
      }
      setBbGifs(all);
    });
    return () => { alive = false; };
  }, []);
  // Auto-publish: the super admin's own library is ADDED to the shared list
  // (debounced) — merged, never replaced, so a browser with a short local
  // list can't wipe what other browsers published.
  const publishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isSuperAdminUser || gifLibrary.length === 0) return;
    if (publishTimerRef.current) clearTimeout(publishTimerRef.current);
    publishTimerRef.current = setTimeout(async () => {
      try {
        const ref = fsDoc(db, 'siteSettings', 'bbGifLibrary');
        const remote: GifEntry[] = await fsGetDoc(ref).then(sn => (sn.exists() ? ((sn.data() as any).gifs || []) : [])).catch(() => []);
        const known = new Set(remote.map(g => g.url));
        const added = gifLibrary.filter(g => g.url && !known.has(g.url));
        if (!added.length) return;
        await fsSetDoc(ref, { gifs: [...remote, ...added], updatedAt: new Date().toISOString() });
      } catch (err) { console.error('BB GIF library publish failed:', err); }
    }, 2000);
    return () => { if (publishTimerRef.current) clearTimeout(publishTimerRef.current); };
  }, [gifLibrary, isSuperAdminUser]);
  // Everyone sees their own gifs + BB's default library (deduped by url);
  // the super admin too, so uploads made on another device show up here.
  const effectiveGifLibrary = [...gifLibrary, ...bbGifs.filter(b => b.url && !gifLibrary.some(g => g.url.split('?')[0] === b.url.split('?')[0]))];
  const effectiveGifLibraryRef = useRef<GifEntry[]>([]);
  effectiveGifLibraryRef.current = effectiveGifLibrary;

  // Per-section pinned GIF URLs (section → url). Empty string / missing = use random.
  const [sectionGifs, setSectionGifsState] = useState<Record<string, string>>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = lsGetSafe(LS_SECTION_GIFS);
        if (stored) return JSON.parse(stored);
      } catch { /* ignore */ }
    }
    return {};
  });

  // GIF skins loaded from Firestore (Super Admin managed)
  const [gifSkins, setGifSkins] = useState<GifSkin[]>([]);
  const gifSkinsRef = useRef<GifSkin[]>([]);
  gifSkinsRef.current = gifSkins;

  useEffect(() => {
    const unsub = subscribeGifSkins((skins: AdminGifSkin[]) => {
      // Map AdminGifSkin → GifSkin (compatible subset)
      const mapped: GifSkin[] = skins
        .filter(s => s.enabled !== false)
        .map(s => ({
          id:          s.id,
          name:        s.name,
          category:    s.category,
          bodyUrl:     s.bodyUrl,
          bodyOpacity: s.bodyOpacity,
          baseHue:     s.baseHue,
          enabled:     s.enabled,
        }));
      setGifSkins(mapped);
    });
    return unsub;
  }, []);

  // Active GIF skin
  const [activeSkinId, setActiveSkinIdState] = useState<string | null>(() => {
    // Visitors start every visit without a skin (see isVisitorNow).
    if (typeof window !== 'undefined' && !isVisitorNow()) {
      return lsGetSafe(LS_ACTIVE_SKIN) ?? null;
    }
    return null;
  });

  // GIF visual controls — opacity, background colour, text colour, bold
  const [gifOpacity, setGifOpacityState] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const v = parseFloat(lsGetSafe(LS_GIF_OPACITY) || '');
      return isNaN(v) ? 0.6 : v;
    }
    return 0.6;
  });
  const gifOpacityRef = useRef<number>(0.6);
  gifOpacityRef.current = gifOpacity;

  const [gifBgColor, setGifBgColorState] = useState<string>(() =>
    typeof window !== 'undefined' ? (lsGetSafe(LS_GIF_BG_COLOR) || '') : '');
  const gifBgColorRef = useRef<string>('');
  gifBgColorRef.current = gifBgColor;

  const [gifTextColor, setGifTextColorState] = useState<string>(() =>
    typeof window !== 'undefined' ? (lsGetSafe(LS_GIF_TEXT_COLOR) || '') : '');
  const gifTextColorRef = useRef<string>('');
  gifTextColorRef.current = gifTextColor;

  const [gifBold, setGifBoldState] = useState<boolean>(() =>
    typeof window !== 'undefined' ? lsGetSafe(LS_GIF_BOLD) === 'true' : false);
  const gifBoldRef = useRef<boolean>(false);
  gifBoldRef.current = gifBold;

  const [gifSize, setGifSizeState] = useState<GifSizeMode>(() =>
    typeof window !== 'undefined'
      ? ((lsGetSafe(LS_GIF_SIZE) as GifSizeMode) || 'cover')
      : 'cover');
  const gifSizeRef = useRef<GifSizeMode>('cover');
  gifSizeRef.current = gifSize;

  // Tile size in px — used only when gifSize === 'tile'. Lets the user set how
  // big each repeated tile is. Per Gordon: "set tile sizes."
  const [gifTileSize, setGifTileSizeState] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const v = parseInt(lsGetSafe(LS_GIF_TILE_SIZE) || '');
      if (!Number.isNaN(v) && v > 0) return v;
    }
    return 200;
  });
  const gifTileSizeRef = useRef<number>(200);
  gifTileSizeRef.current = gifTileSize;

  // Ref to the live applyRandomGifs function so we can call it imperatively
  const applyGifsRef = useRef<(() => void) | null>(null);
  const gifThemeEnabledRef = useRef(false);
  // Background FX: sound-reactive pulse + device-tilt parallax (Gordon:
  // "react on sound and tilt?"). Opt-in, persisted per device.
  const [bgFxSound, setBgFxSoundState] = useState<boolean>(() => lsGetSafe('bb-bgfx-sound') === '1');
  const [bgFxTilt, setBgFxTiltState] = useState<boolean>(() => lsGetSafe('bb-bgfx-tilt') === '1');
  const setBgFxSound = (v: boolean) => { setBgFxSoundState(v); lsSetSafe('bb-bgfx-sound', v ? '1' : '0'); };
  const setBgFxTilt = (v: boolean) => { setBgFxTiltState(v); lsSetSafe('bb-bgfx-tilt', v ? '1' : '0'); };
  useEffect(() => {
    if (!bgFxSound && !bgFxTilt) {
      document.documentElement.style.removeProperty('--bb-bg-pulse');
      document.documentElement.style.removeProperty('--bb-bg-shift-x');
      document.documentElement.style.removeProperty('--bb-bg-shift-y');
      return;
    }
    const root = document.documentElement;
    let tiltX = 0, tiltY = 0;
    const onTilt = (e: DeviceOrientationEvent) => {
      // gamma: left/right (-90..90), beta: front/back (-180..180)
      tiltX = Math.max(-12, Math.min(12, (e.gamma ?? 0) * 0.4));
      tiltY = Math.max(-12, Math.min(12, ((e.beta ?? 0) - 45) * 0.25));
    };
    if (bgFxTilt) window.addEventListener('deviceorientation', onTilt);
    // ~15fps — smooth enough for ambience, cheap enough for iPhone.
    let pulse = 1;
    const id = setInterval(() => {
      if (bgFxSound) {
        const level = (window as any).__bbAudioLevel || 0;
        pulse = pulse * 0.6 + (1.02 + level * 0.05) * 0.4;   // lerp for smoothness
        root.style.setProperty('--bb-bg-pulse', pulse.toFixed(4));
      }
      if (bgFxTilt) {
        root.style.setProperty('--bb-bg-shift-x', `${tiltX.toFixed(1)}px`);
        root.style.setProperty('--bb-bg-shift-y', `${tiltY.toFixed(1)}px`);
      }
    }, 66);
    return () => {
      clearInterval(id);
      if (bgFxTilt) window.removeEventListener('deviceorientation', onTilt);
    };
  }, [bgFxSound, bgFxTilt]);
  useEffect(() => { gifThemeEnabledRef.current = gifThemeEnabled; }, [gifThemeEnabled]);
  useEffect(() => { applyGifsRef.current?.(); }, [gifOpacity, gifBgColor, gifTextColor, gifBold, gifSize, gifTileSize]);

  // Keep a ref so closures inside the mql useEffect always see the latest user.
  const userRef = useRef(user);
  userRef.current = user;

  // Generation counter — incremented on every loadTheme call so that stale
  // async completions (e.g. a previous call with prefersDark=false finishing
  // after a newer call with prefersDark=true has already started) don't
  // overwrite the DOM dark class or theme state.
  const loadThemeGenRef = useRef(0);

  // Detect dark mode preference — THE single source of truth.
  // For LOGGED-IN users: always follow OS preference (localStorage override ignored).
  // For GUESTS: respect saved 'bb-theme-mode' (system / light / dark).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');

    const computePrefersDark = (systemDark: boolean = mql.matches): boolean => {
      if (isVisitorNow()) return visitorPrefersDark(systemDark);
      // Explicit user choice (set by welcome modal or theme picker) always wins.
      // Fall back to OS only when no explicit preference has been saved.
      const saved = lsGetSafe('bb-theme-mode') || 'system';
      if (saved === 'dark')  return true;
      if (saved === 'light') return false;
      return systemDark; // 'system' → follow OS
    };

    setPrefersDark(computePrefersDark());

    // Re-evaluate whenever the OS flips
    const osHandler = (e: MediaQueryListEvent) => setPrefersDark(computePrefersDark(e.matches));
    mql.addEventListener('change', osHandler);

    // Re-evaluate when profile settings change the forced mode.
    // Also force a CSS-var re-apply immediately — even if the boolean value of
    // prefersDark doesn't change (e.g. OS is already light, user explicitly
    // clicks "Light" — same false value, no React state change, no re-render,
    // so the [prefersDark, currentTheme] effect won't fire on its own).
    const modeHandler = () => {
      const dark = computePrefersDark();
      setPrefersDark(dark);
      const theme = currentThemeRef.current;
      if (theme) {
        applyThemeTokensToCss(resolveTokens(theme, dark));
      } else {
        // Theme not loaded yet — apply plain system defaults immediately
        applyThemeTokensToCss(dark ? plainDarkTokens : plainLightTokens);
      }
    };
    window.addEventListener('bb-theme-mode-change', modeHandler);

    return () => {
      mql.removeEventListener('change', osHandler);
      window.removeEventListener('bb-theme-mode-change', modeHandler);
    };
  }, []);

  // ── Appearance mode state (dark / light / system) ─────────────────────────
  const [appearanceMode, setAppearanceModeState] = useState<'dark' | 'light' | 'system'>(() => {
    if (typeof window === 'undefined') return 'system';
    return (lsGetSafe('bb-theme-mode') as 'dark' | 'light' | 'system') || 'system';
  });

  /**
   * Synchronously apply a new appearance mode — updates localStorage, toggles
   * the `dark` CSS class, applies resolved tokens, and updates React state.
   * Used by ThemePicker so the whole app updates on every tap with zero lag.
   */
  const setAppearanceMode = useCallback((mode: 'dark' | 'light' | 'system') => {
    // 1. Persist the choice — always store explicitly so the rotation hook
    //    knows the user has made a deliberate selection (even for 'system').
    //    A visitor's pick lasts this visit only (see isVisitorNow).
    if (isVisitorNow()) visitorModePick = mode;
    else lsSetSafe('bb-theme-mode', mode);

    // 2. Compute the effective dark flag
    const systemDark = typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
    const dark = mode === 'dark' ? true : mode === 'light' ? false : systemDark;

    // 3. Toggle the Tailwind `dark` class immediately
    const root = document.documentElement;
    if (dark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // 4. Apply resolved tokens immediately (synchronous — no async)
    const theme = currentThemeRef.current;
    if (theme) {
      applyThemeTokensToCss(resolveTokens(theme, dark));
    } else {
      applyThemeTokensToCss(dark ? plainDarkTokens : plainLightTokens);
    }

    // 5. Sync React state so the rest of the app (e.g. effects) follows
    setAppearanceModeState(mode);
    setPrefersDark(dark);

    // 6. Dispatch legacy event so any other listeners also update
    window.dispatchEvent(new Event('bb-theme-mode-change'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to theme customizations
  useEffect(() => {
    const unsubscribe = subscribeThemeCustomizations((c) => {
      setCustomizations(c);
    });
    return unsubscribe;
  }, []);

  // Re-apply tokens when OS preference changes (or theme changes).
  // applyThemeTokensToCss now handles the dark-class sync unconditionally —
  // no need to duplicate the lightness check here.
  useEffect(() => {
    if (!currentTheme) return;
    const tokens = resolveTokens(currentTheme, prefersDark);
    applyThemeTokensToCss(tokens); // also toggles .dark class based on bg lightness

    const globalFont = tokens.typography?.fontGlobal;
    if (globalFont && !globalFont.includes('Inter')) {
      loadFont(globalFont);
    }
  }, [prefersDark, currentTheme]);

  // When customizations change, re-apply the current theme without re-fetching
  // anything from Firestore.  Uses refs so this effect only fires on customizations
  // changes — not on every prefersDark/currentTheme change (that's above).
  useEffect(() => {
    const theme = currentThemeRef.current;
    if (!theme) return;
    const themed = applyCustomizations(theme, customizations);
    applyThemeTokensToCss(resolveTokens(themed, prefersDarkRef.current));
  }, [customizations]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTheme = useCallback(async () => {
    // Stamp this invocation; if a newer call starts before this one finishes,
    // the stale completion will see gen !== loadThemeGenRef.current and bail.
    const gen = ++loadThemeGenRef.current;
    setLoading(true);
    try {
      // Priority: User Theme > Platform Default
      // NOTE: Page-specific themes are intentionally disabled — they caused the
      // theme to change automatically on every page navigation, which was jarring.
      // Admins can still set a global platform theme from Super Admin → Themes.
      let theme: Theme | null = null;

      if (user) {
        // 2a. Logged-in: use profileThemeId from userProfile if set
        const profileThemeId = userProfile?.profileThemeId;
        if (profileThemeId) {
          const localPreset = themePresets.find(p => p.id === profileThemeId);
          if (localPreset) {
            theme = {
              id: localPreset.id,
              name: localPreset.name,
              tokens: localPreset.tokens,
              darkTokens: localPreset.darkTokens,
              lightTokens: (localPreset as any).lightTokens,
            } as Theme;
          }
        }
        // 2b. Fall back to platform default for logged-in users with no preference
        if (!theme) {
          theme = await getDefaultPlatformTheme();
        }
      }
      // 2c. Visitors: no stored guest theme, no platform default — the System
      // preset below, dark or light by the system setting (see isVisitorNow). The one exception is an
      // accessibility theme: someone who needs it shouldn't have to find it
      // again on every visit (Gordon 2026-11-02, low vision).
      if (!user && !theme) {
        const guestId = lsGetSafe(LS_GUEST_THEME);
        const a11y = guestId ? themePresets.find(p => p.id === guestId && p.category === 'accessibility') : undefined;
        if (a11y) {
          theme = { id: a11y.id, name: a11y.name, tokens: a11y.tokens, darkTokens: a11y.darkTokens, lightTokens: (a11y as any).lightTokens } as Theme;
        }
      }

      // 4. Ultimate fallback: use System preset (follows OS light/dark, pure B&W)
      if (!theme) {
        const systemPreset = themePresets.find(p => p.id === 'system');
        if (systemPreset) {
          theme = {
            id: 'system',
            name: 'System',
            tokens: systemPreset.tokens,
            darkTokens: systemPreset.darkTokens,
          } as Theme;
        }
      }

      // Apply customizations to the found theme (use ref so customizations
      // is NOT in loadTheme's dep array — prevents re-fetching Firestore on
      // every onSnapshot tick, which was causing the "theme keeps changing" bug)
      if (theme) {
        theme = applyCustomizations(theme, customizationsRef.current);
      }

      // Bail out if a newer loadTheme call has already started — applying stale
      // tokens here would overwrite the dark/light class set by the newer call.
      if (gen !== loadThemeGenRef.current) return;

      setCurrentTheme(theme);

      if (theme && theme.tokens) {
        // For logged-in users on the system preset: wipe any leftover
        // custom-theme inline styles first so globals.css `:root` vars take over.
        if (user && (!theme.id || theme.id === 'system')) {
          clearThemeInlineStyles();
        }
        // Apply the correct tokens based on the CURRENT dark preference.
        // Use prefersDarkRef.current (not the closure value of prefersDark) so
        // that if the user toggles light/dark while this async fetch was in flight,
        // we always honour their latest choice rather than the stale closure value.
        const tokens = resolveTokens(theme, prefersDarkRef.current);
        applyThemeTokensToCss(tokens);

        // Load custom font if specified
        const globalFont = tokens.typography?.fontGlobal;
        if (globalFont && !globalFont.includes('Inter')) {
          await loadFont(globalFont);
        }
      }
    } catch (error) {
      console.error('Error loading theme:', error);
    } finally {
      // Only clear loading if this is still the most-recent invocation.
      if (gen === loadThemeGenRef.current) setLoading(false);
    }
  // prefersDark intentionally NOT in deps — we use prefersDarkRef.current above so
  // changes to dark/light mode do not trigger a full theme re-fetch from Firestore.
  // The [prefersDark, currentTheme] effect below re-applies tokens synchronously
  // whenever the user toggles the mode, which is sufficient.
  // customizations intentionally NOT in deps — use customizationsRef.current inside.
  // Putting it here caused a full Firestore re-fetch on every onSnapshot tick.
  // pathname intentionally NOT in deps — page-specific themes are disabled to prevent
  // the theme from jumping on every navigation.
  }, [user, userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  // When a guest logs in, immediately wipe any random rotation CSS and apply
  // the system (OS dark/light) preset before loadTheme finishes async work.
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prevId = prevUserIdRef.current;
    prevUserIdRef.current = user?.uid ?? null;
    // Signed out → the visitor look: drop any skin from the page (the saved
    // choice stays on the device for their next sign-in) and go dark.
    if (prevId && !user) {
      visitorModePick = null;
      clearGifSkinFromDom();
      window.dispatchEvent(new Event('bb-theme-mode-change'));
    }
    // Transition from guest (null) → logged-in
    if (prevId === null && user) {
      clearGifSkin();
      // Clear all leftover custom-theme inline CSS vars, then apply system preset
      clearThemeInlineStyles();
      const systemPreset = themePresets.find(p => p.id === 'system');
      if (systemPreset?.tokens) {
        applyThemeTokensToCss(resolveTokens(
          { id: 'system', name: 'System', tokens: systemPreset.tokens } as Theme,
          prefersDark
        ));
      }
      // Visitors were forced dark — now their own light/dark choice applies.
      window.dispatchEvent(new Event('bb-theme-mode-change'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load global appearance settings (thumbnail radius etc.) once on mount
  useEffect(() => {
    getAppearanceSettings().then(applyAppearanceCss).catch(() => {});
  }, []);

  // ── Color rotation interval ────────────────────────────────────────────────
  useEffect(() => {
    // Never rotate colors: guests see a stable dark/light theme.
    if (!user) return;
    if (!colorRotationEnabled || !currentTheme) return;

    const tokens = resolveTokens(currentTheme, prefersDark);
    const colors = tokens.colors;
    if (!colors) return; // guard: theme loaded without colors (e.g. malformed Firestore doc)
    const interval = setInterval(() => {
      // Rotate hues subtly only — NEVER auto-invert lightness. Flipping the whole
      // app light↔dark on a 5s timer was disorienting on mobile
      // (Damon: "the app changes themes to light and dark on its own").
      hueOffsetRef.current = (hueOffsetRef.current + 15) % 360;
      const rotated = shiftAllHues(colors, hueOffsetRef.current);
      applyThemeTokensToCss({ ...tokens, colors: rotated });
    }, 5000);

    return () => {
      clearInterval(interval);
      // Restore original theme when rotation is disabled
      if (currentTheme) {
        const tok = resolveTokens(currentTheme, prefersDark);
        applyThemeTokensToCss(tok);
      }
      hueOffsetRef.current = 0;
      invertedRef.current = false;
    };
  }, [colorRotationEnabled, currentTheme, prefersDark]);

  // ── GIF background rotation interval ──────────────────────────────────────
  useEffect(() => {
    // Never apply GIF backgrounds for guests or logged-in users without GIF skins enabled.
    // Guests always see a stable theme — no random cycling.
    if (!user) {
      applyGifsRef.current = null;
      const existingStyle = document.getElementById('theme-gif-styles');
      if (existingStyle) existingStyle.remove();
      return;
    }
    if (!gifThemeEnabled || gifLibrary.length === 0) {
      applyGifsRef.current = null;
      // Clear GIF backgrounds when disabled
      if (!gifThemeEnabled) {
        const existingStyle = document.getElementById('theme-gif-styles');
        if (existingStyle) existingStyle.remove();
      }
      return;
    }

    const sectionSelectorsMap: Record<string, string> = {
      body:    '[data-bg="body"]',
      header:  '[data-bg="header"]',
      sidebar: '[data-bg="sidebar"]',
      card:    '[data-bg="card"]',
      player:  '[data-bg="player"]',
    };

    function applyRandomGifs() {
      const opacity = gifOpacityRef.current;
      const bgColor = gifBgColorRef.current || 'transparent';
      const textRule = gifTextColorRef.current ? `color: ${gifTextColorRef.current} !important;` : '';
      const boldRule = gifBoldRef.current ? 'font-weight: bold !important;' : '';
      const sizeMode = gifSizeRef.current;
      const cssRules: string[] = [];

      // Translate size mode → CSS background properties
      let bgSize: string;
      let bgRepeat: string;
      let bgPosition: string;
      switch (sizeMode) {
        case 'tile':
          // Repeated tiles at the user-set px size (square). Per Gordon:
          // "set tile sizes."
          bgSize = `${gifTileSizeRef.current}px`;
          bgRepeat = 'repeat';
          bgPosition = 'top left';
          break;
        case 'fill':
          // Stretch to fill exactly — may distort aspect ratio.
          bgSize = '100% 100%';
          bgRepeat = 'no-repeat';
          bgPosition = 'center';
          break;
        case 'contain':
          bgSize = 'contain';
          bgRepeat = 'no-repeat';
          bgPosition = 'center';
          break;
        case 'original':
          bgSize = 'auto';
          bgRepeat = 'no-repeat';
          bgPosition = 'center';
          break;
        default: // cover
          bgSize = 'cover';
          bgRepeat = 'no-repeat';
          bgPosition = 'center';
      }

      for (const section of GIF_SECTIONS) {
        // Use pinned GIF if set, otherwise random from library. Guard the
        // pinned URL: if a section is pinned to a GIF that's since been
        // deleted from the library, fall back to random instead of showing
        // the ghost of a removed GIF (Gordon: "they stay even if I erase
        // them").
        const pinnedUrl = sectionGifs[section];
        const pinnedStillExists = pinnedUrl && gifLibrary.some(g => g.url === pinnedUrl);
        const url = (pinnedStillExists ? pinnedUrl : null)
          || gifLibrary[Math.floor(Math.random() * gifLibrary.length)]?.url;
        if (!url) continue;

        const sel = sectionSelectorsMap[section];
        // CRITICAL: the GIF lives on a z-index:-1 ::after, so the host's OWN
        // background must be transparent or it paints OVER the GIF — that's
        // why sidebar/header/player/cards were "stuck on color". Force the
        // host transparent so the negative-z GIF is visible; content (normal
        // flow) renders above it.
        //
        // Clip sections: card / header / player get overflow:hidden +
        // border-radius:inherit on the ::after so the GIF covers cleanly
        // inside the card's rounded box. body / sidebar are NOT clipped
        // (overflow:hidden there breaks sticky filter bars + scroll).
        const clip = section === 'card' || section === 'header' || section === 'player';
        const tintLayer = (bgColor && bgColor !== 'transparent')
          ? `, linear-gradient(${bgColor}, ${bgColor})`
          : '';
        const bodyText = section === 'body' ? `${textRule} ${boldRule}` : '';
        cssRules.push(
          `${sel} { position: relative !important; isolation: isolate; background-color: transparent !important; ${clip ? 'overflow: hidden;' : ''} ${bodyText} }`,
          `${sel}::after {`,
          `  content: '';`,
          `  position: absolute;`,
          `  inset: 0;`,
          `  ${clip ? 'border-radius: inherit;' : ''}`,
          `  background-image: url('${url}')${tintLayer};`,
          `  background-size: ${bgSize}${tintLayer ? ', cover' : ''};`,
          `  background-position: ${bgPosition}${tintLayer ? ', center' : ''};`,
          `  background-repeat: ${bgRepeat}${tintLayer ? ', no-repeat' : ''};`,
          `  opacity: ${opacity};`,
          `  z-index: -1;`,
          `  pointer-events: none;`,
          `  animation: gifFade 1s ease-in-out;`,
          `  transform: translate3d(var(--bb-bg-shift-x, 0px), var(--bb-bg-shift-y, 0px), 0) scale(var(--bb-bg-pulse, 1));`,
          `  transition: transform 90ms linear;`,
          `  will-change: transform;`,
          `}`
        );
      }

      // ── Card thumbnails must stay SOLID — never reveal the GIF behind ──
      // Per Gordon: "card thumbnails ... go to a transparent fade so one sees
      // the background behind the thumbnail. don't do that. don't let the card
      // bg show thru in any way." Card content sits over the (now transparent)
      // card host with the GIF behind, so any fade-in / hover opacity on a
      // thumbnail reveals the GIF through it. Force every image inside a card
      // fully opaque with a solid backing so nothing ever shows through.
      cssRules.push(
        `[data-bg="card"] img, [data-bg="card"] video { opacity: 1 !important; animation: none !important; transition: none !important; }`,
        `[data-bg="card"] .aspect-square, [data-bg="card"] [class*="thumb"] { background-color: hsl(var(--card)) !important; }`
      );

      // Remove old GIF style
      const old = document.getElementById('theme-gif-styles');
      if (old) old.remove();

      if (cssRules.length > 0) {
        const style = document.createElement('style');
        style.id = 'theme-gif-styles';
        style.textContent = `
          @keyframes gifFade { from { opacity: 0; } to { opacity: ${opacity}; } }
          ${cssRules.join('\n')}
        `;
        document.head.appendChild(style);
      }
    }

    // Expose so `randomizeGifSections` can call it imperatively
    applyGifsRef.current = applyRandomGifs;

    applyRandomGifs(); // Apply immediately
    const interval = setInterval(applyRandomGifs, 20000); // Rotate every 20 seconds

    return () => {
      clearInterval(interval);
      applyGifsRef.current = null;
      const style = document.getElementById('theme-gif-styles');
      if (style) style.remove();
    };
  }, [gifThemeEnabled, gifLibrary, sectionGifs]);

  // ── Public actions ────────────────────────────────────────────────────────
  const setColorRotationEnabled = (enabled: boolean) => {
    setColorRotationEnabledState(enabled);
    lsSetSafe(LS_COLOR_ROTATION, String(enabled));
    hueOffsetRef.current = 0;
    invertedRef.current = false;
    countryStepRef.current = {};
    // Immediately restore original theme when disabling
    if (!enabled && currentTheme) {
      const tokens = resolveTokens(currentTheme, prefersDark);
      applyThemeTokensToCss(tokens);
    }
  };

  /** Advance one rotation step immediately (same logic as the 5-second auto-interval) */
  function rotateOneStep() {
    if (!currentTheme) return;
    const tokens = resolveTokens(currentTheme, prefersDark);
    const colors = tokens.colors;
    if (!colors) return; // guard: malformed theme

    // Country themes: cycle through 3 distinct flag-colour layouts
    const preset = themePresets.find(p => p.id === currentTheme.id);
    if ((preset as { category?: ThemeCategory } | undefined)?.category === 'country') {
      const prev = countryStepRef.current[currentTheme.id] ?? 0;
      const next = (prev + 1) % 3;
      countryStepRef.current[currentTheme.id] = next;
      const rotated = buildCountryVariant(colors, next);
      applyThemeTokensToCss({ ...tokens, colors: rotated });
      return;
    }

    // Every other theme: its own three colours swap roles, 4th click = the original.
    const prev = countryStepRef.current[currentTheme.id] ?? 0;
    const next = (prev + 1) % 3;
    countryStepRef.current[currentTheme.id] = next;
    applyThemeTokensToCss({ ...tokens, colors: buildRoleRotation(colors, next) });
  }

  const setGifThemeEnabled = (enabled: boolean) => {
    setGifThemeEnabledState(enabled);
    lsSetSafe(LS_GIF_ENABLED, String(enabled));
  };

  const updateGifLibrary = (gifs: GifEntry[]) => {
    // Shared BB entries (id prefix bbgif:) never persist into a user's OWN
    // library — they live in siteSettings and re-merge on load.
    const own = gifs.filter(g => !String(g.id).startsWith('bbgif:'));
    setGifLibrary(own);
    lsSetSafe(LS_GIF_LIBRARY, JSON.stringify(own));
    // When a GIF is removed from the library, also unpin it from any section
    // that pointed at it — otherwise the deleted GIF keeps showing as a
    // section background (Gordon: "they stay even if I erase them"). Also
    // immediately clear the injected style if the library is now empty so the
    // backgrounds disappear the moment the last GIF is erased.
    const liveUrls = new Set(gifs.map(g => g.url));
    setSectionGifsState(prev => {
      const next: Record<string, string> = {};
      let changed = false;
      for (const [section, url] of Object.entries(prev)) {
        if (liveUrls.has(url)) next[section] = url;
        else changed = true;
      }
      if (changed) lsSetSafe(LS_SECTION_GIFS, JSON.stringify(next));
      return changed ? next : prev;
    });
    if (gifs.length === 0) {
      const style = document.getElementById('theme-gif-styles');
      if (style) style.remove();
    }
  };

  const setSectionGif = useCallback((section: string, url: string | null) => {
    setSectionGifsState(prev => {
      const next = { ...prev };
      if (url) {
        next[section] = url;
      } else {
        delete next[section];
      }
      lsSetSafe(LS_SECTION_GIFS, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearSectionGifs = useCallback(() => {
    setSectionGifsState({});
    lsRemoveSafe(LS_SECTION_GIFS);
  }, []);

  /** Immediately randomize GIFs across all sections */
  const randomizeGifSections = useCallback(() => {
    applyGifsRef.current?.();
  }, []);

  // ── GIF visual-control setters ────────────────────────────────────────────
  const setGifOpacity = useCallback((v: number) => {
    setGifOpacityState(v);
    lsSetSafe(LS_GIF_OPACITY, String(v));
    gifOpacityRef.current = v;
    // Re-apply immediately
    if (activeSkinId) {
      const skin = gifSkinsRef.current.find(s => s.id === activeSkinId);
      if (skin) applyGifSkinToDom(skin, v, gifBgColorRef.current, gifTextColorRef.current, gifBoldRef.current);
    } else {
      applyGifsRef.current?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSkinId]);

  const setGifBgColor = useCallback((v: string) => {
    setGifBgColorState(v);
    v ? lsSetSafe(LS_GIF_BG_COLOR, v) : lsRemoveSafe(LS_GIF_BG_COLOR);
    gifBgColorRef.current = v;
    if (activeSkinId) {
      const skin = gifSkinsRef.current.find(s => s.id === activeSkinId);
      if (skin) applyGifSkinToDom(skin, gifOpacityRef.current, v, gifTextColorRef.current, gifBoldRef.current);
    } else {
      applyGifsRef.current?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSkinId]);

  const setGifTextColor = useCallback((v: string) => {
    setGifTextColorState(v);
    v ? lsSetSafe(LS_GIF_TEXT_COLOR, v) : lsRemoveSafe(LS_GIF_TEXT_COLOR);
    gifTextColorRef.current = v;
    if (activeSkinId) {
      const skin = gifSkinsRef.current.find(s => s.id === activeSkinId);
      if (skin) applyGifSkinToDom(skin, gifOpacityRef.current, gifBgColorRef.current, v, gifBoldRef.current);
    } else {
      applyGifsRef.current?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSkinId]);

  const setGifBold = useCallback((v: boolean) => {
    setGifBoldState(v);
    lsSetSafe(LS_GIF_BOLD, String(v));
    gifBoldRef.current = v;
    if (activeSkinId) {
      const skin = gifSkinsRef.current.find(s => s.id === activeSkinId);
      if (skin) applyGifSkinToDom(skin, gifOpacityRef.current, gifBgColorRef.current, gifTextColorRef.current, v);
    } else {
      applyGifsRef.current?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSkinId]);

  const setGifSize = useCallback((v: GifSizeMode) => {
    setGifSizeState(v);
    lsSetSafe(LS_GIF_SIZE, v);
    gifSizeRef.current = v;
    applyGifsRef.current?.();
  }, []);

  const setGifTileSize = useCallback((v: number) => {
    const clamped = Math.max(16, Math.min(1200, Math.round(v)));
    setGifTileSizeState(clamped);
    lsSetSafe(LS_GIF_TILE_SIZE, String(clamped));
    gifTileSizeRef.current = clamped;
    applyGifsRef.current?.();
  }, []);

  // ── GIF Skin public actions ───────────────────────────────────────────────
  const applyGifSkin = useCallback((skinId: string, opts?: { persist?: boolean }) => {
    const skin = gifSkinsRef.current.find(s => s.id === skinId);
    if (!skin) return;
    const persist = opts?.persist !== false;

    // Apply base color theme so skin has a matching palette
    const hue = skin.baseHue;
    const bg    = `${hue} 28% 9%`;
    const fg    = `${hue} 10% 94%`;
    const card  = `${hue} 22% 13%`;
    const border = `${hue} 18% 22%`;
    const muted  = `${hue} 18% 16%`;
    const primHue = (hue + 150) % 360;
    const primary = `${primHue} 70% 55%`;
    // Primary is always at 55% lightness — needs dark text for WCAG AA compliance
    const primaryFg = `0 0% 5%`;
    const sbBg = `${hue} 32% 7%`;

    const partial = {
      background: bg, foreground: fg, card, cardForeground: fg,
      border, muted, mutedForeground: `${hue} 10% 60%`,
      primary, primaryForeground: primaryFg,
      // Accent at 58% L — dark text for readability
      accent: `${(hue + 60) % 360} 65% 58%`, accentForeground: `0 0% 5%`,
      secondary: muted, secondaryForeground: fg,
      sidebarBackground: sbBg, sidebarForeground: fg,
      sidebarPrimary: primary, sidebarPrimaryForeground: primaryFg,
      sidebarAccent: `${hue} 22% 14%`, sidebarAccentForeground: fg,
      headerBackground: `${hue} 28% 11%`, playerBackground: `${hue} 28% 7%`,
      playerToggleBackground: primary,
    };

    if (currentTheme) {
      const tokens = resolveTokens(currentTheme, prefersDark);
      applyThemeTokensToCss({ ...tokens, colors: { ...tokens.colors, ...partial } as typeof tokens.colors });
    }

    // Mount fixed GIF backdrop
    applyGifSkinToDom(skin, gifOpacity, gifBgColor, gifTextColor, gifBold);
    setActiveSkinIdState(skinId);
    // Ephemeral applications (e.g. the start page's per-visit random skin)
    // do NOT persist — next visit rolls a fresh one.
    if (persist) lsSetSafe(LS_ACTIVE_SKIN, skinId);
  }, [currentTheme, prefersDark, gifOpacity, gifBgColor, gifTextColor, gifBold]);

  const applyRandomGifSkin = useCallback((opts?: { persist?: boolean }) => {
    const skins = gifSkinsRef.current.filter(s => s.enabled !== false);
    if (skins.length > 0) {
      const idx = Math.floor(Math.random() * skins.length);
      applyGifSkin(skins[idx].id, opts);
      return;
    }
    // No curated Image Skins → draw from the GIF library (BB shared + own).
    // Synthesize an ephemeral skin: the gif becomes the body backdrop and a
    // hue derived from the URL tints the palette so each gif feels themed.
    const pool = effectiveGifLibraryRef.current.filter(g => g.url);
    if (pool.length === 0) return;
    const g = pool[Math.floor(Math.random() * pool.length)];
    let hash = 0;
    for (let i = 0; i < g.url.length; i++) hash = ((hash << 5) - hash + g.url.charCodeAt(i)) | 0;
    const pseudo: GifSkin = {
      id: `bbgif-rand-${Math.abs(hash)}`,
      name: g.label || 'BB GIF',
      category: 'BB',
      bodyUrl: g.url,
      baseHue: Math.abs(hash) % 360,
      enabled: true,
    };
    // Temporarily register so applyGifSkin's lookup finds it, then apply.
    gifSkinsRef.current = [...gifSkinsRef.current, pseudo];
    applyGifSkin(pseudo.id, opts);
    gifSkinsRef.current = gifSkinsRef.current.filter(sk => sk.id !== pseudo.id);
  }, [applyGifSkin]);

  // Any picture or video URL as a one-off backdrop (same palette rule as skins).
  const applyUrlBackdrop = useCallback((url: string, name: string, opts?: { persist?: boolean }) => {
    let hash = 0;
    for (let i = 0; i < url.length; i++) hash = ((hash << 5) - hash + url.charCodeAt(i)) | 0;
    const pseudo: GifSkin = { id: `bbbg-${Math.abs(hash)}`, name, category: 'BB', bodyUrl: url, baseHue: Math.abs(hash) % 360, enabled: true };
    gifSkinsRef.current = [...gifSkinsRef.current, pseudo];
    applyGifSkin(pseudo.id, { ...opts, persist: false }); // a one-off URL is never the saved skin
    gifSkinsRef.current = gifSkinsRef.current.filter(sk => sk.id !== pseudo.id);
  }, [applyGifSkin]);

  const lastBackdropRef = useRef('');
  const applyRandomBackdrop = useCallback(async (opts?: { persist?: boolean }) => {
    const skins = gifSkinsRef.current.filter(s => s.enabled !== false);
    const gifs = effectiveGifLibraryRef.current.filter(g => g.url).map(g => ({ url: g.url, name: g.label || 'BB GIF' }));
    const [art, videos] = await Promise.all([loadArtPool(), loadVideoPool()]);
    const sources: { url: string; name: string; skinId?: string }[][] = [
      skins.map(sk => ({ url: sk.bodyUrl, name: sk.name, skinId: sk.id })), gifs, art, videos,
    ].filter(list => list.length > 0);
    if (!sources.length) return;
    // A kind first (so 200 covers don't drown 5 GIFs), then one of it — never the same twice running.
    const list = sources[Math.floor(Math.random() * sources.length)];
    let pick = list[Math.floor(Math.random() * list.length)];
    if (pick.url === lastBackdropRef.current && list.length > 1) pick = list[(list.indexOf(pick) + 1) % list.length];
    lastBackdropRef.current = pick.url;
    if (pick.skinId) applyGifSkin(pick.skinId, opts);
    else applyUrlBackdrop(pick.url, pick.name, opts);
  }, [applyGifSkin, applyUrlBackdrop]);
  const applyRandomBackdropRef = useRef(applyRandomBackdrop);
  applyRandomBackdropRef.current = applyRandomBackdrop;

  // Background demo — remembered for signed-in people; a visitor's pick lasts the visit.
  const [bgDemo, setBgDemoState] = useState(false);
  useEffect(() => { if (!isVisitorNow()) setBgDemoState(lsGetSafe('bb-bg-demo') === '1'); }, []);
  const setBgDemo = useCallback((on: boolean) => {
    setBgDemoState(on);
    if (!isVisitorNow()) lsSetSafe('bb-bg-demo', on ? '1' : '0');
  }, []);
  useEffect(() => {
    bgDrift = bgDemo;
    if (!bgDemo) {
      const el = document.getElementById(GIF_BACKDROP_ID);
      if (el && el.tagName === 'DIV' && !el.querySelector('iframe,video')) el.style.animation = '';
      return;
    }
    void applyRandomBackdropRef.current({ persist: false });
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void applyRandomBackdropRef.current({ persist: false });
    }, BG_DEMO_MS);
    return () => clearInterval(timer);
  }, [bgDemo]);

  // Random GIF skin rotation every 3 clicks (Gordon) — only for people who
  // haven't chosen their own look: a saved skin or a personal profile theme
  // switches this off entirely. Ephemeral applications (no persistence).
  const clickRotateRef = useRef(0);
  const userProfileForClicksRef = useRef<typeof userProfile>(null);
  userProfileForClicksRef.current = userProfile;
  useEffect(() => {
    const handler = () => {
      try {
        if (isVisitorNow()) return;                                             // visitors: no colour changes
        if (lsGetSafe(LS_ACTIVE_SKIN)) return;                                  // user picked a skin
        if ((userProfileForClicksRef.current as any)?.profileThemeId) return;   // personal theme
        if (gifThemeEnabledRef.current) return;                                 // user runs their OWN section backgrounds
        if (gifSkinsRef.current.filter(sk => sk.enabled !== false).length === 0
            && effectiveGifLibraryRef.current.filter(g => g.url).length === 0) return;
        clickRotateRef.current = (clickRotateRef.current + 1) % 3;
        if (clickRotateRef.current === 0) applyRandomGifSkin({ persist: false });
      } catch { /* never break clicks */ }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [applyRandomGifSkin]);


  const clearGifSkin = useCallback(() => {
    clearGifSkinFromDom();
    setActiveSkinIdState(null);
    lsRemoveSafe(LS_ACTIVE_SKIN);
    // Re-apply current theme colours
    if (currentTheme) {
      const tokens = resolveTokens(currentTheme, prefersDark);
      applyThemeTokensToCss(tokens);
    }
  }, [currentTheme, prefersDark]);

  // Re-mount backdrop when gifSkins are loaded from Firestore and an active skin is saved
  useEffect(() => {
    if (activeSkinId && gifSkins.length > 0 && user) {
      const skin = gifSkins.find(s => s.id === activeSkinId);
      if (skin) {
        const opacity  = parseFloat(lsGetSafe(LS_GIF_OPACITY) || '') || 0.6;
        const bgColor  = lsGetSafe(LS_GIF_BG_COLOR)   || '';
        const txtColor = lsGetSafe(LS_GIF_TEXT_COLOR)  || '';
        const bold     = lsGetSafe(LS_GIF_BOLD) === 'true';
        applyGifSkinToDom(skin, opacity, bgColor, txtColor, bold);
      }
    }
  // Re-run when gifSkins load (Firestore async), activeSkinId changes or they sign in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gifSkins, activeSkinId, user]);

  /**
   * Generate and apply a random high-contrast colour theme without changing the
   * saved/preset theme — just paints CSS variables directly for exploration.
   */
  function applyRandomColorTheme() {
    // Seasons, nature, pastel, jewel, vintage, sunset, ink and fluorescent —
    // never the same family twice in a row; text, icons and outlines always
    // high contrast (Gordon 2026-11-02; lib/random-palette.ts).
    const roll = randomPalette(Math.random, lastPaletteFamilyRef.current);
    lastPaletteFamilyRef.current = roll.family;
    const partial = roll.colors;

    if (currentTheme) {
      const tokens = resolveTokens(currentTheme, prefersDark);
      const newColors = { ...(tokens.colors ?? {}), ...partial };
      applyThemeTokensToCss({
        ...tokens,
        colors: newColors as typeof tokens.colors,
        // Random colour themes must never use a serif font
        typography: { ...(tokens.typography ?? {}), fontGlobal: "'Inter', sans-serif" },
      });
    } else {
      // No theme yet — route through applyThemeTokensToCss so fixContrastTokens runs
      applyThemeTokensToCss({
        colors: partial as unknown as ColorTokens,
        typography: { fontGlobal: "'Inter', sans-serif" },
      } as unknown as ThemeTokens);
    }
  }

  async function setUserTheme(themeId: string | null) {
    // Reset rotation state when switching themes
    hueOffsetRef.current = 0;
    invertedRef.current = false;
    countryStepRef.current = {};

    // ── Guest (logged-out) path: use localStorage ──────────────────────────
    if (!user) {
      if (themeId) {
        lsSetSafe(LS_GUEST_THEME, themeId);
        const localPreset = themePresets.find(p => p.id === themeId);
        if (localPreset) {
          const themeObj = applyCustomizations({
            id: localPreset.id,
            name: localPreset.name,
            tokens: localPreset.tokens,
            darkTokens: localPreset.darkTokens,
            lightTokens: (localPreset as any).lightTokens,
          } as Theme, customizations);
          // forceOwnTokens: always apply the chosen theme's colours, never plain fallback
          const tokens = resolveTokens(themeObj, prefersDark, true);
          applyThemeTokensToCss(tokens);
          setCurrentTheme(themeObj);
          const globalFont = tokens.typography?.fontGlobal;
          if (globalFont && !globalFont.includes('Inter')) loadFont(globalFont);
        }
      } else {
        lsRemoveSafe(LS_GUEST_THEME);
        // Actually CLEAR the applied theme too — removing the stored id alone
        // left currentTheme state + the inline token styles active (context
        // effects kept re-applying the old theme until a full reload).
        setCurrentTheme(null);
        clearThemeInlineStyles();
        applyThemeTokensToCss(prefersDark ? plainDarkTokens : plainLightTokens);
      }
      return;
    }

    // ── Authenticated path: persist to Firestore ───────────────────────────
    try {
      // Apply tokens immediately for instant visual feedback (no round-trip wait)
      if (themeId) {
        const localPreset = themePresets.find(p => p.id === themeId);
        if (localPreset) {
          const themeWithCustom = applyCustomizations({
            id: localPreset.id,
            name: localPreset.name,
            tokens: localPreset.tokens,
            darkTokens: localPreset.darkTokens,
            lightTokens: (localPreset as any).lightTokens,
          } as Theme, customizations);

          // forceOwnTokens: always apply the chosen theme's colours, never plain fallback
          const tokens = resolveTokens(themeWithCustom, prefersDark, true);
          applyThemeTokensToCss(tokens);
          setCurrentTheme(themeWithCustom);
          const globalFont = tokens.typography?.fontGlobal;
          if (globalFont && !globalFont.includes('Inter')) loadFont(globalFont);
        }
      }

      // Persist to Firestore in background
      await setUserProfileTheme(user.uid, themeId);
    } catch (error) {
      console.error('Error setting user theme:', error);
      throw error;
    }
  }

  async function reloadTheme() {
    await loadTheme();
  }

  return (
    <ThemeContext.Provider value={{
      currentTheme,
      setUserTheme,
      reloadTheme,
      loading,
      colorRotationEnabled,
      setColorRotationEnabled,
      rotateOneStep,
      applyRandomColorTheme,
      gifThemeEnabled,
      gifLibrary: effectiveGifLibrary,
      bgFxSound, setBgFxSound, bgFxTilt, setBgFxTilt,
      sectionGifs,
      setGifThemeEnabled,
      updateGifLibrary,
      setSectionGif,
      clearSectionGifs,
      randomizeGifSections,
      gifSkins,
      activeSkinId,
      applyGifSkin,
      applyRandomGifSkin,
      applyRandomBackdrop,
      bgDemo,
      setBgDemo,
      clearGifSkin,
      gifOpacity,
      gifBgColor,
      gifTextColor,
      gifBold,
      setGifOpacity,
      setGifBgColor,
      setGifTextColor,
      setGifBold,
      gifSize,
      setGifSize,
      gifTileSize,
      setGifTileSize,
      setAppearanceMode,
      appearanceMode,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
